/**
 * XM writer. Lossless for anything the editor can represent.
 */
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  CELL_VOLUME,
  LOOP_NONE,
  LOOP_PINGPONG,
  type Song,
} from '../types';
import { ByteWriter } from '../loaders/binary';
import type { SaveResult } from './modWriter';

/**
 * FastTracker II writes 263 here and some players hard-code that expectation
 * even though the field is self-describing, so match it byte for byte.
 */
const INSTRUMENT_HEADER_SIZE = 263;
const SAMPLE_HEADER_SIZE = 40;

export function saveXm(song: Song): SaveResult {
  const warnings: string[] = [];
  const w = new ByteWriter(1 << 20);

  if (song.channels > 64) {
    throw new Error(`XM supports at most 64 channels; this song has ${song.channels}.`);
  }

  // --- Header -------------------------------------------------------------
  w.str('Extended Module: ', 17, 0x20);
  w.str(song.name.slice(0, 20), 20, 0x20);
  w.u8(0x1a);
  w.str('FinTracker', 20, 0x20);
  w.u16le(0x0104);

  const songLength = Math.min(256, song.songLength);
  // Header size counts from offset 60: 20 fixed bytes plus the 256-entry order.
  w.u32le(20 + 256);
  w.u16le(songLength);
  w.u16le(song.restartPosition);
  w.u16le(song.channels);

  let patternCount = 0;
  for (let i = 0; i < songLength; i++) patternCount = Math.max(patternCount, song.order[i] ?? 0);
  patternCount = Math.max(1, Math.min(256, patternCount + 1));

  w.u16le(patternCount);
  w.u16le(song.instruments.length);
  w.u16le(song.linearFrequency ? 1 : 0);
  w.u16le(song.initialSpeed);
  w.u16le(song.initialTempo);
  for (let i = 0; i < 256; i++) w.u8(song.order[i] ?? 0);

  // --- Patterns -----------------------------------------------------------
  for (let p = 0; p < patternCount; p++) {
    const pattern = song.patterns[p];
    const rows = pattern ? pattern.rows : 64;

    const packed = new ByteWriter(rows * song.channels * 5);
    if (pattern) {
      for (let row = 0; row < rows; row++) {
        for (let ch = 0; ch < song.channels; ch++) {
          const off = (row * song.channels + ch) * CELL_STRIDE;
          const note = pattern.data[off + CELL_NOTE];
          const instrument = pattern.data[off + CELL_INSTRUMENT];
          const volume = pattern.data[off + CELL_VOLUME];
          const effect = pattern.data[off + CELL_EFFECT];
          const param = pattern.data[off + CELL_PARAM];

          if (note === 0 && instrument === 0 && volume === 0 && effect === 0 && param === 0) {
            packed.u8(0x80); // empty cell: mask byte with no fields set
            continue;
          }

          let mask = 0x80;
          if (note) mask |= 0x01;
          if (instrument) mask |= 0x02;
          if (volume) mask |= 0x04;
          if (effect) mask |= 0x08;
          if (param) mask |= 0x10;

          packed.u8(mask);
          if (note) packed.u8(note);
          if (instrument) packed.u8(instrument);
          if (volume) packed.u8(volume);
          if (effect) packed.u8(effect);
          if (param) packed.u8(param);
        }
      }
    }

    const packedData = packed.finish();
    w.u32le(9); // pattern header length
    w.u8(0); // packing type
    w.u16le(rows);
    w.u16le(packedData.length);
    w.raw(packedData);
  }

  // --- Instruments --------------------------------------------------------
  for (const inst of song.instruments) {
    const samples = inst.samples;

    if (samples.length === 0) {
      // An empty instrument is just the short header.
      w.u32le(29);
      w.str(inst.name.slice(0, 22), 22);
      w.u8(0);
      w.u16le(0);
      continue;
    }
    if (samples.length > 16) {
      warnings.push(`Instrument "${inst.name}" has ${samples.length} samples; XM allows 16.`);
    }
    const kept = samples.slice(0, 16);

    const start = w.length;
    w.u32le(INSTRUMENT_HEADER_SIZE);
    w.str(inst.name.slice(0, 22), 22);
    w.u8(0);
    w.u16le(kept.length);
    w.u32le(SAMPLE_HEADER_SIZE);
    for (let i = 0; i < 96; i++) w.u8(Math.min(kept.length - 1, inst.sampleMap[i] ?? 0));

    writeEnvelopePoints(w, inst.volumeEnvelope.points);
    writeEnvelopePoints(w, inst.panningEnvelope.points);

    w.u8(Math.min(12, inst.volumeEnvelope.points.length));
    w.u8(Math.min(12, inst.panningEnvelope.points.length));
    w.u8(inst.volumeEnvelope.sustainPoint);
    w.u8(inst.volumeEnvelope.loopStart);
    w.u8(inst.volumeEnvelope.loopEnd);
    w.u8(inst.panningEnvelope.sustainPoint);
    w.u8(inst.panningEnvelope.loopStart);
    w.u8(inst.panningEnvelope.loopEnd);
    w.u8(envelopeTypeByte(inst.volumeEnvelope));
    w.u8(envelopeTypeByte(inst.panningEnvelope));
    w.u8(inst.vibratoType);
    w.u8(inst.vibratoSweep);
    w.u8(inst.vibratoDepth);
    w.u8(inst.vibratoRate);
    w.u16le(inst.volumeFadeout);

    // Pad out to the declared header size.
    while (w.length - start < INSTRUMENT_HEADER_SIZE) w.u8(0);

    // Sample headers first, then all sample data — XM's layout, not interleaved.
    const encoded: Uint8Array[] = [];
    for (const smp of kept) {
      const is16 = smp.bits === 16;
      const bytes = encodeDelta(smp.data, smp.length, is16);
      encoded.push(bytes);

      let type = 0;
      if (smp.loopType !== LOOP_NONE && smp.loopLength > 0) {
        type |= smp.loopType === LOOP_PINGPONG ? 2 : 1;
      }
      if (is16) type |= 0x10;

      const shift = is16 ? 1 : 0;
      w.u32le(smp.length << shift);
      w.u32le(smp.loopStart << shift);
      w.u32le(smp.loopLength << shift);
      w.u8(Math.max(0, Math.min(64, smp.volume)));
      w.u8(smp.finetune & 0xff);
      w.u8(type);
      w.u8(Math.max(0, Math.min(255, smp.panning)));
      w.u8(smp.relativeNote & 0xff);
      w.u8(0);
      w.str(smp.name.slice(0, 22), 22);
    }
    for (const bytes of encoded) w.raw(bytes);
  }

  return { data: w.finish(), warnings };
}

function writeEnvelopePoints(w: ByteWriter, points: { x: number; y: number }[]): void {
  for (let i = 0; i < 12; i++) {
    const p = points[i];
    w.u16le(p ? p.x : 0);
    w.u16le(p ? p.y : 0);
  }
}

function envelopeTypeByte(env: { enabled: boolean; sustainEnabled: boolean; loopEnabled: boolean }): number {
  return (env.enabled ? 1 : 0) | (env.sustainEnabled ? 2 : 0) | (env.loopEnabled ? 4 : 0);
}

/**
 * Delta-encode sample data, mirroring the loader's wrapping accumulator so a
 * load/save round trip is exact.
 */
function encodeDelta(data: Float32Array, length: number, is16: boolean): Uint8Array {
  if (is16) {
    const out = new Uint8Array(length * 2);
    let prev = 0;
    for (let i = 0; i < length; i++) {
      const v = Math.max(-1, Math.min(1, data[i] ?? 0));
      const q = Math.max(-32768, Math.min(32767, Math.round(v * 32768)));
      const delta = (q - prev) & 0xffff;
      out[i * 2] = delta & 0xff;
      out[i * 2 + 1] = (delta >> 8) & 0xff;
      prev = q;
    }
    return out;
  }
  const out = new Uint8Array(length);
  let prev = 0;
  for (let i = 0; i < length; i++) {
    const v = Math.max(-1, Math.min(1, data[i] ?? 0));
    const q = Math.max(-128, Math.min(127, Math.round(v * 128)));
    out[i] = (q - prev) & 0xff;
    prev = q;
  }
  return out;
}
